import { Autocomplete, Divider, Grid, TextField, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import { ClientAccountTypeForSearch, ServiceType, GetFilteredClientEntity, ClientStatusType, ClientFilterDateRange } from '../../store/types';
import { EnumerationMultiSelect, ToggleButtonItem, WO2Checkbox, WO2ToggleButtonGroup } from 'src/common';
import WO2Button from 'src/common/components/button/Button';
import { Props } from '../../container';
import { useDebounce } from 'src/common/hooks';
import DateRangePicker from 'src/common/components/DateRangePicker';
import { DateRange } from 'src/store';
import { DatePickerNames, DatePickerTypes } from 'src/common/components/DateRangePickerPreDefinedDates';

const serviceTypeToggleButtons: ToggleButtonItem<string>[] = [
  { name: 'All', value: '' },
  { name: ServiceType.Mda.displayName, value: ServiceType.Mda.name },
  { name: ServiceType.Roa.displayName, value: ServiceType.Roa.name },
];

interface ReportingGenerationFormProps extends Props {
  setFlagReset: (value: boolean) => void;
}

export const ReportingGenerationForm = ({
  bulk,
  fetchFilteredReportClientsProgress,
  setServiceTypeParameter,
  setClientTypesParameter,
  fetchFilteredReportClients,
  setSelectedAfsls,
  setSelectedGroups,
  setSelectedStatuses,
  setSelectedClients,
  setSelectedPractices,
  setSelectedAdvisers,
  setAllowTerminatedClient,
  discardCsvUpload,
  setFlagReset,
}: ReportingGenerationFormProps) => {
  const [searchText, setSearchText] = useState<string>('');
  const [searchEntity, setSearchEntity] = useState<string>('');
  const lastMonth = DateTime.now().minus({ months: 1 });
  const lastMonthDateRange: ClientFilterDateRange = {
    fromDate: lastMonth.startOf('month').toISODate(),
    toDate: lastMonth.endOf('month').toISODate(),
    dateRangeType: DatePickerNames.LastMonth,
  };
  const [exitDate, setExitDate] = useState<ClientFilterDateRange | undefined>(undefined);
  const [inceptionDate, setInceptionDate] = useState<ClientFilterDateRange | undefined>(undefined);
  const [inactiveOnly, setInactiveOnly] = useState<boolean>((bulk.selectedStatuses ?? []).some((status) => status !== ClientStatusType.Active.name));

  const searchTextDebounce = useDebounce<string>(searchText, 500);

  const toPickerDateRange = (range?: ClientFilterDateRange): DateRange => ({
    dateFrom: range?.fromDate ?? '',
    dateTo: range?.toDate ?? '',
    dateRangeType: range?.dateRangeType ?? DatePickerTypes.DateRange,
  });

  const toFilterDateRange = (range: DateRange): ClientFilterDateRange => ({
    fromDate: range.dateFrom,
    toDate: range.dateTo,
    dateRangeType: range.dateRangeType,
  });

  const updateExitDate = (range: DateRange | undefined) => {
    setExitDate(range?.dateFrom && range?.dateTo ? toFilterDateRange(range) : undefined);
  };

  const updateInceptionDate = (range: DateRange | undefined) => {
    setInceptionDate(range?.dateFrom && range?.dateTo ? toFilterDateRange(range) : undefined);
  };

  const handleInactiveOnlyToggle = (checked: boolean) => {
    setInactiveOnly(checked);
    setAllowTerminatedClient(checked);
    setFlagReset(true);
    if (checked) {
      setSelectedStatuses((bulk.selectedStatuses ?? []).filter((status) => status !== ClientStatusType.Active.name));
    } else {
      setSelectedStatuses([ClientStatusType.Active.name]);
      updateExitDate(undefined);
    }
  };

  useEffect(() => {
    const activeSearchText = inactiveOnly && searchEntity === 'Group' ? '' : searchTextDebounce;
    fetchFilteredReportClients({
      afslIds: bulk.selectedAfsls?.map((value: GetFilteredClientEntity) => value.entityCoreId) ?? [],
      practiceIds: bulk.selectedPractices?.map((value: GetFilteredClientEntity) => value.entityCoreId) ?? [],
      adviserIds: bulk.selectedAdvisers?.map((value: GetFilteredClientEntity) => value.entityCoreId) ?? [],
      accountTypes: bulk.clientTypes ?? [],
      clientStatus: inactiveOnly ? bulk.selectedStatuses ?? [] : [ClientStatusType.Active.name],
      serviceTypes: bulk.serviceType ? [bulk.serviceType] : [],
      searchText: activeSearchText || undefined,
      searchEntity: activeSearchText ? searchEntity : undefined,
      exitDate: inactiveOnly && exitDate?.fromDate && exitDate?.toDate ? exitDate : undefined,
      inceptionDate: inceptionDate?.fromDate && inceptionDate?.toDate ? inceptionDate : undefined,
    });
  }, [
    searchTextDebounce,
    bulk.selectedAfsls,
    bulk.serviceType,
    bulk.clientTypes,
    bulk.selectedPractices,
    bulk.selectedAdvisers,
    bulk.selectedClients,
    bulk.selectedGroups,
    bulk.selectedStatuses,
    inactiveOnly,
    exitDate,
    inceptionDate,
  ]);

  useEffect(() => {
    if (inactiveOnly) {
      if (bulk.selectedGroups.length > 0) {
        setSelectedGroups([]);
      }
      if (searchEntity === 'Group') {
        setSearchText('');
      }
    }
  }, [inactiveOnly, bulk.selectedGroups]);

  useEffect(() => {
    if (!inactiveOnly && (bulk.selectedStatuses ?? []).length === 0) {
      setSelectedStatuses([ClientStatusType.Active.name]);
    }
  }, []);

  const handleClientSearch = () => {
    fetchFilteredReportClients({
      afslIds: bulk.selectedAfsls?.map((value: GetFilteredClientEntity) => value.entityCoreId) ?? [],
      practiceIds: bulk.selectedPractices?.map((value: GetFilteredClientEntity) => value.entityCoreId) ?? [],
      adviserIds: bulk.selectedAdvisers?.map((value: GetFilteredClientEntity) => value.entityCoreId) ?? [],
      accountTypes: bulk.clientTypes ?? [],
      clientStatus: inactiveOnly ? bulk.selectedStatuses ?? [] : [ClientStatusType.Active.name],
      serviceTypes: bulk.serviceType ? [bulk.serviceType] : [],
      exitDate: inactiveOnly && exitDate?.fromDate && exitDate?.toDate ? exitDate : undefined,
      inceptionDate: inceptionDate?.fromDate && inceptionDate?.toDate ? inceptionDate : undefined,
    });
    discardCsvUpload();
  };

  return (
    <Grid item container direction="column" xs={12} sm={4}>
      <Grid item container direction="column" paddingRight="10px">
        <Grid item>
          <Autocomplete
            noOptionsText="No items to select"
            id="afsl-multiselect"
            multiple
            fullWidth
            disabled={bulk.csvUploaded || bulk.selectedClients.length > 0 || bulk.selectedGroups.length > 0}
            options={bulk.afsls}
            loading={fetchFilteredReportClientsProgress.isLoading}
            value={bulk.selectedAfsls}
            onInputChange={(e, value, reason) => {
              if (reason === 'input') {
                setSearchEntity('AfslLicensee');
                setSearchText(value);
              }
            }}
            getOptionLabel={(option: GetFilteredClientEntity) => option.name}
            renderInput={(params) => <TextField {...params} margin="normal" label="AFSL" fullWidth />}
            onChange={(_e: React.SyntheticEvent<Element, Event>, values: GetFilteredClientEntity[]) => {
              setSelectedAfsls(values);
              setFlagReset(true);
            }}
            isOptionEqualToValue={(option: GetFilteredClientEntity, value: GetFilteredClientEntity) => option.entityCoreId === value.entityCoreId}
          />
        </Grid>
        <Grid item>
          <Autocomplete
            noOptionsText="No items to select"
            id="practice-multiselect"
            fullWidth
            multiple
            options={bulk.practices}
            value={bulk.selectedPractices}
            loading={fetchFilteredReportClientsProgress.isLoading}
            onInputChange={(e, value, reason) => {
              if (reason === 'input') {
                setSearchEntity('Practice');
                setSearchText(value);
              }
            }}
            getOptionLabel={(option: GetFilteredClientEntity) => option.name}
            renderInput={(params) => <TextField {...params} margin="normal" label="Practice" fullWidth />}
            onChange={(_e: React.SyntheticEvent<Element, Event>, values: GetFilteredClientEntity[]) => {
              setSelectedPractices(values);
              setFlagReset(true);
            }}
            isOptionEqualToValue={(option: GetFilteredClientEntity, value: GetFilteredClientEntity) => option.entityCoreId === value.entityCoreId}
          />
        </Grid>
        <Grid item>
          <Autocomplete
            noOptionsText="No items to select"
            id="adviser-multiselect"
            fullWidth
            multiple
            options={bulk.advisers}
            value={bulk.selectedAdvisers}
            loading={fetchFilteredReportClientsProgress.isLoading}
            onInputChange={(e, value, reason) => {
              if (reason === 'input') {
                setSearchEntity('Adviser');
                setSearchText(value);
              }
            }}
            getOptionLabel={(option: GetFilteredClientEntity) => option.name}
            renderInput={(params) => <TextField {...params} margin="normal" label="Adviser" fullWidth />}
            onChange={(_e: React.SyntheticEvent<Element, Event>, values: GetFilteredClientEntity[]) => {
              setSelectedAdvisers(values);
              setFlagReset(true);
            }}
            isOptionEqualToValue={(option: GetFilteredClientEntity, value: GetFilteredClientEntity) => option.entityCoreId === value.entityCoreId}
          />
        </Grid>
        <Grid item>
          <EnumerationMultiSelect
            label="Account Type"
            type={ClientAccountTypeForSearch}
            value={bulk.clientTypes}
            onChange={setClientTypesParameter}
          />
        </Grid>
        <Grid item container direction="column" spacing={1} mt={1}>
          <Grid item>
            <Typography variant="h4">Service Type</Typography>
          </Grid>
          <Grid item>
            <WO2ToggleButtonGroup
              fullWidth={true}
              buttons={serviceTypeToggleButtons}
              value={bulk.serviceType}
              onChangeHandler={(value: string) => {
                setServiceTypeParameter(value);
                setFlagReset(true);
              }}
            />
          </Grid>
        </Grid>
        {!inactiveOnly && (
          <Grid item>
            <Autocomplete
              noOptionsText="No items to select"
              id="group-multiselect"
              multiple
              fullWidth
              disabled={bulk.csvUploaded}
              options={bulk.groups}
              value={bulk.selectedGroups}
              loading={fetchFilteredReportClientsProgress.isLoading}
              onInputChange={(e, value, reason) => {
                if (reason === 'input') {
                  setSearchEntity('Group');
                  setSearchText(value);
                }
              }}
              getOptionLabel={(option: GetFilteredClientEntity) => `${option.name} (${option.entityCoreId})`}
              renderInput={(params) => <TextField {...params} margin="normal" label="Group Search" fullWidth />}
              onChange={(_e: React.SyntheticEvent<Element, Event>, values: GetFilteredClientEntity[]) => {
                setSelectedGroups(values);
              }}
              isOptionEqualToValue={(option: GetFilteredClientEntity, value: GetFilteredClientEntity) => option.entityCoreId === value.entityCoreId}
            />
          </Grid>
        )}
        <Grid item>
          <Autocomplete
            noOptionsText="No items to select"
            id="client-multiselect"
            multiple
            fullWidth
            disabled={bulk.csvUploaded}
            options={bulk.clients}
            value={bulk.selectedClients}
            getOptionLabel={(option: GetFilteredClientEntity) => `${option.name} (${option.entityCoreId})`}
            loading={fetchFilteredReportClientsProgress.isLoading}
            onInputChange={(e, value, reason) => {
              if (reason === 'input') {
                setSearchEntity('Client');
                setSearchText(value);
              }
            }}
            renderInput={(params) => <TextField {...params} margin="normal" label="Client Search" fullWidth />}
            onChange={(_e: React.SyntheticEvent<Element, Event>, values: GetFilteredClientEntity[]) => {
              setSelectedClients(values);
            }}
            isOptionEqualToValue={(option: GetFilteredClientEntity, value: GetFilteredClientEntity) => option.entityCoreId === value.entityCoreId}
          />
        </Grid>
      </Grid>
      <Grid item mt={3}>
        <DateRangePicker
          dateRange={toPickerDateRange(inceptionDate)}
          dateRangeLabel="Inception Date"
          setDateRange={updateInceptionDate}
          onRemove={() => updateInceptionDate(undefined)}
          emptyRangeDefault={toPickerDateRange(lastMonthDateRange)}
        />
      </Grid>
      {inactiveOnly && (
        <>
          <Grid item mt={1} paddingRight="10px">
            <EnumerationMultiSelect
              label="Status"
              type={ClientStatusType}
              hidden={[ClientStatusType.Active.name]}
              value={bulk.selectedStatuses ?? []}
              onChange={(values: string[]) => {
                setSelectedStatuses(values);
                setFlagReset(true);
              }}
            />
          </Grid>
          <Grid item mt={3}>
            <DateRangePicker
              dateRange={toPickerDateRange(exitDate)}
              dateRangeLabel="Exit Date"
              setDateRange={updateExitDate}
              onRemove={() => updateExitDate(undefined)}
              emptyRangeDefault={toPickerDateRange(lastMonthDateRange)}
            />
          </Grid>
        </>
      )}
      <Grid item mt={3}>
        <Divider />
      </Grid>
      <Grid item mt={1}>
        <WO2Checkbox label="Search for inactive clients" checked={inactiveOnly} onChangeHandler={handleInactiveOnlyToggle} />
      </Grid>
      <Grid item alignSelf="flex-end" mt={1}>
        <WO2Button onClick={handleClientSearch} variant="contained">
          Search
        </WO2Button>
      </Grid>
    </Grid>
  );
};
